import React, { useEffect, useState } from "react";
import { getFiles, archiveFiles, removeFromIndexAndBucket, saveFiles, completeMultipartUpload, generatePresignedUrl, startMultipartUpload, generateSinglePreSignedUrl, downloadFile } from "../api/index.js";
import Table from "../reusable_components/Table.jsx";
import HeaderSection from "./HeaderSection";
import Pagination from "../reusable_components/Pagination.jsx";
import { toast } from "react-toastify";
import moment from "moment";
import Model from "../reusable_components/Model.jsx";
import { btnCancel, btnOne } from "../utils/style.js";
import { setLoader } from "../reusable_components/Loader.js";
import axios from "axios"


const DirectoryFiles = () => {
  const [files, setFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [filterData, setFilterData] = useState({});
  const [reloadTable, setReloadTable] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null);

  const handleRowSelect = (rowIndex) => {
    const updatedFiles = [...files];
    const rowId = updatedFiles[rowIndex].id;

    updatedFiles[rowIndex].selected = !updatedFiles[rowIndex].selected;

    setSelectedRows((prevSelectedRows) => {
      if (updatedFiles[rowIndex].selected) {
        return [...prevSelectedRows, rowId];
      } else {
        return prevSelectedRows.filter((id) => id !== rowId);
      }
    });

    setFiles(updatedFiles);
  };

  const fetchFiles = async (page = 1, filterQuery) => {
    setLoader(true)

    try {
      const archivedStatus = false;
      const response = await getFiles(page, archivedStatus, filterQuery);
      response?.data?.data?.forEach((record) => {
        const age = moment().diff(moment(record.publication_date), "months");
        record.age =
          age > 24
            ? "> 2 years"
            : age > 12
              ? "> 1 year"
              : age > 6
                ? "> 6 months"
                : "> 1 month";
        record.publication_date = moment(record.publication_date).format(
          "MM-DD-YYYY"
        );
        record.createdAt = moment(record.createdAt).format("MM-DD-YYYY");
      });

      setFiles(response.data?.data || []);
      setTotalPages(response.data?.pagination?.totalPages || 1);
    } catch (err) {
      toast.error("Error loading data")
    } finally {
      setLoader(false)
    }
  };

  const handleArchive = async () => {
    try {
      const data = { ids: selectedRows, archivedStatus: true };
      const result = await archiveFiles(data);
      if (result.status === 200) {
        setSelectedRows([]);
        toast.success(`${result.data.message} File Archived`, { autoClose: 3000 });
        setReloadTable(true)
      } else {
        return toast.error(result.data.message, { autoClose: 3000 });
      }
    } catch (err) {
      toast.error("Failed to archive");
    }
  };

  const handleDownload = async () => {
    try {
      const fileId = selectedRows[0];
      const response = await downloadFile(fileId);
      if (response.status === 200) {
        setSelectedRows([]);
        const link = document.createElement("a");
        link.href = response.data.url;
        link.setAttribute("download", response.data.fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error("Failed to download", { autoClose: 3000 });
      }
    }
    catch (error) {
      console.error("Error downloading file:", error);
    }
  }


  const handleModify = async () => {
    setShowModal(true);
  };

  const handleFileUpload = async (formData) => {
    try {
      setLoader(true);
      const resp = await removeFromIndexAndBucket(selectedRows[0]);
      if (!resp.data) {
        return toast.error("could not update")
      }
      const result = { success: [], fail: [], alreadyExist: [] }
      const indexFiles = []

      const updatedName = resp.data.file_name
      const file = selectedFile
      if (file.size < 10000000) {
        // Call your API to get the presigned URL
        const response = await generateSinglePreSignedUrl({ fileName: updatedName })

        const { url } = response.data;
        // Use the presigned URL to upload the file
        const uploadResponse = await axios.put(url, file, {
          headers: {
            "Content-Type": file.type,
          },
        });

        if (uploadResponse.status === 200) {
          result.success.push(file.name)
          indexFiles.push({ file_name: updatedName, file_size: file.size })
        } else {
          result.fail.push(file.name)
        }

      } else {
        // call multipart upload endpoint and get uploadId
        const response = await startMultipartUpload({ fileName: updatedName, contentType: file.type });

        // get uploadId
        let { uploadId } = response.data;

        // get total size of the file
        let totalSize = file.size;
        // set chunk size to 7MB
        let chunkSize = 7000000;
        // calculate number of chunks
        let numChunks = Math.ceil(totalSize / chunkSize);

        // generate presigned urls
        let presignedUrls_response = await generatePresignedUrl({ fileName: updatedName, uploadId: uploadId, partNumbers: numChunks });

        let presigned_urls = presignedUrls_response?.data?.presignedUrls;

        // upload the file into chunks to different presigned url
        let parts = [];
        const uploadPromises = [];

        for (let i = 0; i < numChunks; i++) {
          let start = i * chunkSize;
          let end = Math.min(start + chunkSize, totalSize);
          let chunk = file.slice(start, end);
          let presignedUrl = presigned_urls[i];

          uploadPromises.push(
            axios.put(presignedUrl, chunk, {
              headers: {
                "Content-Type": file.type,
              },
            })
          );
        }

        const uploadResponses = await Promise.all(uploadPromises);

        uploadResponses.forEach((response, i) => {
          // existing response handling

          parts.push({
            etag: response.headers.etag,
            PartNumber: i + 1,
          });
        });

        // make a call to multipart complete api
        let complete_upload = await completeMultipartUpload({ fileName: updatedName, uploadId: uploadId, parts: parts });

        // if upload is successful, alert user
        if (complete_upload.status === 200) {
          result.success.push(file.name)
          indexFiles.push({ file_name: updatedName, file_size: file.size })
        } else {
          result.fail.push(file.name)
        }
      }

      const formInput = { ...resp.data }
      if (result.success.length > 0) {
        saveFiles({ formInput, indexFiles })
        toast.success(`Success: ${result.success.join(", ")}`, { autoClose: 3000 })
      }

    } catch (error) {
      console.error(error);
      toast.error("Failed to update", { autoClose: 3000 });
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchFiles(currentPage, filterQuery);
    setSelectedRows([]);
    setFiles((prevFiles) =>
      prevFiles.map((file) => ({ ...file, selected: false }))
    );
  }, [currentPage, filterQuery, reloadTable]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const columns = [
    { header: "File", key: "file_name", width: "20%", isFilterEnable: true },
    { header: "Make", key: "manufacturer", width: "15%", isFilterEnable: true },
    { header: "Model", key: "model", width: "15%", isFilterEnable: true },
    { header: "Year", key: "year", width: "10%", isFilterEnable: true },
    { header: "Is-Index", key: "indexStatus", width: "15%" },
    { header: "Uploaded At", key: "createdAt", width: "15%" },
    { header: "Age", key: "age", width: "10%" },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <HeaderSection title={"Files"} />
        <div className="ml-auto flex space-x-2">
          <button
            disabled={selectedRows.length !== 1}
            className={`px-3 py-1 text-sm rounded-md 
        ${selectedRows.length !== 1
                ? `${btnOne} text-white cursor-not-allowed opacity-50`
                : `${btnOne} text-white hover:scale-110 transform transition-all duration-200`
              }`}
            onClick={handleDownload}
          >
            Download
          </button>
          <button
            disabled={selectedRows.length !== 1}
            className={`px-3 py-1 text-sm rounded-md 
        ${selectedRows.length !== 1
                ? `${btnOne} text-white cursor-not-allowed opacity-50`
                : `${btnOne} text-white hover:scale-110 transform transition-all duration-200`
              }`}
            onClick={handleModify}
          >
            Update
          </button>
          <button
            disabled={selectedRows.length < 1}
            className={`px-3 py-1 text-sm rounded-md 
        ${selectedRows.length < 1
                ? `${btnCancel} text-white cursor-not-allowed opacity-80`
                : `${btnCancel} text-white hover:scale-110 transform transition-all duration-200`
              }`}
            onClick={handleArchive}
          >
            Archive
          </button>
        </div>
      </div>
      {showModal && (
        <Model
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          setShowModal={setShowModal}
          handleFileUpload={handleFileUpload}
        />
      )}
      <Table data={files} columns={columns} onRowSelect={handleRowSelect} setFilterQuery={setFilterQuery} filterData={filterData} setFilterData={setFilterData} />
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default DirectoryFiles;
